import { useMemo } from "react";
import { DateTime } from "luxon";

import { DataTypeName, DataTypeNameWithBuyersAndSuppliers } from "lib/types/models";

export const availableDataTypes: DataTypeName[] = [
  "TENDERS|PRE-TENDERS",
  "AWARDS",
  "BUYERS|SUPPLIERS",
  "CONTACTS",
  "FRAMEWORKS",
  "DOCUMENTS",
  "BIDS",
  "MARKET_ANALYSIS",
];

// Utility functions
export function getActiveDataTypes(): DataTypeName[] {
  return window.currentUser?.active_data_types ?? [];
}

export function getSubscribedDataTypes(): DataTypeName[] {
  return window.currentUser?.subscribed_data_types ?? [];
}

export function getTrialDate(dateType: "trial_start" | "trial_end"): Date | null {
  const dateString = window.currentUser?.company[dateType];
  return dateString ? new Date(dateString) : null;
}

export function getCompanyPaymentType(): string | null {
  return (window.currentUser?.company.analytics_tags?.company_payment_type as string) ?? null;
}

export function sortDataTypes(dataTypes: DataTypeName[]): void {
  dataTypes.sort((a, b) => availableDataTypes.indexOf(a) - availableDataTypes.indexOf(b));
}

const toDataTypeName = (t: DataTypeNameWithBuyersAndSuppliers): DataTypeName =>
  t === "BUYERS" || t === "SUPPLIERS" ? "BUYERS|SUPPLIERS" : t;

function createHasDataTypes(activeDataTypes: DataTypeName[]) {
  return (...dataTypes: DataTypeNameWithBuyersAndSuppliers[]): boolean => {
    const missing = dataTypes.filter(
      (type) => activeDataTypes.indexOf(toDataTypeName(type)) === -1,
    );

    if (dataTypes.includes("CONTACTS")) {
      return missing.length === 0;
    } else {
      return !!window.guestUser || missing.length === 0;
    }
  };
}

function createHasSubscribedDataTypes(subscribedDataTypes: DataTypeName[]) {
  return (...dataTypes: DataTypeNameWithBuyersAndSuppliers[]): boolean => {
    return dataTypes.every((type) => subscribedDataTypes.includes(toDataTypeName(type)));
  };
}

function createGetMissingDataTypes(activeDataTypes: DataTypeName[]) {
  return (
    ...dataTypes: DataTypeNameWithBuyersAndSuppliers[]
  ): DataTypeNameWithBuyersAndSuppliers[] => {
    return dataTypes.filter((type) => activeDataTypes.indexOf(toDataTypeName(type)) === -1);
  };
}

export function isEligibleForTrial(
  trialEndDate: Date | null,
  companyPaymentType: string | null,
): boolean {
  return !trialEndDate && companyPaymentType !== "Paid";
}

export function isOnTrial(trialEndDate: Date | null): boolean {
  if (!trialEndDate) return false;
  const endOfTrialTime = DateTime.fromJSDate(trialEndDate).toUTC().endOf("day");
  const now = DateTime.now().toUTC();
  return now <= endOfTrialTime;
}

export function getTrialStatus(
  trialEndDate: Date | null = getTrialDate("trial_start"),
  companyPaymentType: string | null = getCompanyPaymentType(),
): "On Trial" | "Eligible" | "Ineligible" {
  if (isOnTrial(trialEndDate)) {
    return "On Trial";
  } else if (isEligibleForTrial(trialEndDate, companyPaymentType)) {
    return "Eligible";
  } else {
    return "Ineligible";
  }
}

export function useSubscription() {
  // memoise for reference equality
  return useMemo(() => {
    const activeDataTypes = getActiveDataTypes();
    const subscribedDataTypes = getSubscribedDataTypes();
    const trialStartDate = getTrialDate("trial_start");
    const trialEndDate = getTrialDate("trial_end");
    const companyPaymentType = getCompanyPaymentType();
    const hasDataTypes = createHasDataTypes(activeDataTypes);
    const hasSubscribedDataTypes = createHasSubscribedDataTypes(subscribedDataTypes);
    const getMissingDataTypes = createGetMissingDataTypes(activeDataTypes);
    const deactivatedDataTypes = availableDataTypes.filter(
      (type) => !activeDataTypes.includes(type),
    );

    return {
      activatedDataTypes: activeDataTypes,
      deactivatedDataTypes,
      hasDataTypes,
      hasSubscribedDataTypes,
      getMissingDataTypes,
      trialEndDate,
      trialStartDate,
      companyPaymentType,
      eligibleForTrial: isEligibleForTrial(trialEndDate, companyPaymentType),
      isOnTrial: isOnTrial(trialEndDate),
      trialStatus: getTrialStatus(trialEndDate, companyPaymentType),
    };
  }, []);
}
